<template>
  <div class="redeem-completed__container">
    <SuccessCurtain v-if="showCurtain" class="redeem-completed__curtain" />
    <template v-if="hasRedeemStored">
      <div class="redeem-completed__navbar">
        <IconButton
          icon-button
          type="button"
          aria-label="fechar"
          icon-name="ic_cancel"
          data-testid="icon-btn-back"
          @click="goBackToWallet"
        />
      </div>
      <div class="redeem-completed__content">
        <section class="redeem-completed__title">
          <RoundedIcon
            icon-name="ic_check_simple"
            background-size="64"
            :main-color="NebraskaColors.statusPositiveBackground"
            :text-color="NebraskaColors.statusPositive"
            aria-hidden="true"
          />
          <Heading as="h1" :level="1">Resgate solicitado</Heading>
          <BaseText
            v-if="showApprovalText"
            size="md"
            :color="NebraskaColors.textSecondary"
          >
            A aprovação do seu cliente pela plataforma é necessária para a
            conclusão do resgate
          </BaseText>
        </section>
        <section class="redeem-completed__cards-content">
          <ResumeCard
            :items="summaryFormatted"
            status-message="Este é um valor aproximado. O valor final do resgate pode mudar um pouco, dependendo do valor de venda das cotas."
          />
          <StatusInformationCard />
          <ResumeCard :items="productsFormatted" />
        </section>
      </div>
      <Footer
        left-button-text="Voltar para carteira"
        @on-left-button-click="goBackToWallet"
      />
    </template>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import {
  Heading,
  IconButton,
  RoundedIcon,
  BaseText
} from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { StatusInformationCard } from './components/StatusInformationCard';
import { IResumeCardItem, ResumeCard } from '../../components/ResumeCard';
import { Footer } from '../../components/FooterBtnContainer';
import { SuccessCurtain } from './components/SuccessCurtain';
import { namespace } from 'vuex-class';
import { ILastRedeemInfoPayload } from '../../store/redeem/redeem-types';
import { trackViewLoad, trackBackToPortfoliosView } from './track-events';
import { isImpersonate } from '@/modules/common/services/partner';
import {
  StorageKeys,
  getUserB2B
} from '@/modules/common/services/local-storage';
import { B2bPartnerType } from '@/types/models/Customer/AdvisorProfile';
import { PortfolioCategories } from '@/types/models/Portfolio';

const redeemModule = namespace('RedeemModule');

@Component({
  components: {
    Heading,
    ResumeCard,
    Footer,
    IconButton,
    RoundedIcon,
    StatusInformationCard,
    SuccessCurtain,
    BaseText
  }
})
export default class WealthProductWithdrawCompleted extends Vue {
  @redeemModule.Mutation('clearRedeemInformation')
  private readonly clearRedeemInformation!: () => void;

  @redeemModule.Getter('lastRedeemData')
  public readonly lastRedeemPortfolioInfo!: ILastRedeemInfoPayload;

  @Prop({ type: String, required: true })
  private readonly portfolioId!: string;

  public readonly NebraskaColors = NebraskaColors;
  public showCurtain = true;

  beforeMount() {
    if (!this.hasRedeemStored) {
      this.goBackToWallet();
    } else {
      trackViewLoad();
    }
  }

  mounted() {
    setTimeout(() => {
      this.showCurtain = false;
    }, 3000);
  }

  public goBackToWallet() {
    this.clearRedeemInformation();
    trackBackToPortfoliosView();
    this.$router.replace({
      name: 'wealthPortfolio',
      params: { id: this.portfolioId, type: PortfolioCategories.Custom }
    });
  }

  get isPartnerManager(): boolean {
    const partner = getUserB2B();
    return partner?.advisorProfile?.type === B2bPartnerType.MANAGER;
  }

  get hasRedeemStored() {
    return (
      this.lastRedeemPortfolioInfo && this.lastRedeemPortfolioInfo.portfolio
    );
  }

  get summaryFormatted(): IResumeCardItem[] {
    const { summaryPreview } = this.lastRedeemPortfolioInfo;
    return summaryPreview
      ? [
          {
            title: 'Resumo',
            nestedItems: [
              { label: 'Destino', value: 'Conta Digital' },
              {
                label: 'Total bruto solicitado',
                value: summaryPreview.grossValue.formatted
              }
            ],
            total: {
              label: 'Total líquido estimado',
              value: summaryPreview.netValue.formatted
            }
          }
        ]
      : [];
  }

  get productsFormatted(): IResumeCardItem[] {
    const { products } = this.lastRedeemPortfolioInfo;
    return products
      ? products.map(product => ({
          title: product.name,
          nestedItems: [
            {
              label: 'Liquidação',
              value: product.liquidationDate
            },
            {
              label: 'Valor líquido estimado',
              value: product.netValue.formatted
            }
          ]
        }))
      : [];
  }

  get showApprovalText(): boolean {
    return (
      isImpersonate() &&
      localStorage.getItem(StorageKeys.IsBrandOwner) === 'false' &&
      !this.isPartnerManager
    );
  }
}
</script>
<style lang="less" scoped>
@screenMaxWidth: 800px;
.redeem-completed {
  &__navbar {
    height: 64px;
    width: 100%;
    padding: 0px @size-spacing-x600;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    .to-md({ justify-content: flex-start; padding: 0px @size-spacing-x400; });
  }
  &__content {
    max-width: @screenMaxWidth;
    margin: @size-spacing-x1000 auto;
    padding-bottom: @size-spacing-x1800;
    .to-md({ padding: 0px @size-spacing-x400; margin: @size-spacing-x500 auto; });
  }
  &__title {
    width: 100%;
    max-width: 500px;
    margin: 0 auto @size-spacing-x800;
    display: flex;
    flex-direction: column;
    gap: @size-spacing-x400;
    align-items: center;
    text-align: center;
    .to-md({ align-items: flex-start; });
  }
  &__cards-content {
    display: flex;
    flex-direction: column;
    gap: @size-spacing-x500;
  }
  &__curtain {
    height: 100vh;
  }
}
</style>
